import React, {useCallback, useEffect, useRef, useState} from 'react';
import {getServerTime} from "../../services/time";
import {numberWordSuffix} from "../../utils/word";

// Формирует части таймера по оставшимся миллисекундам.
const buildTimeLeft = (millisecondsLeft) => {
    const safeMillisecondsLeft = Math.max(millisecondsLeft, 0);

    return {
        hours: Math.floor((safeMillisecondsLeft / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((safeMillisecondsLeft / 1000 / 60) % 60),
        seconds: Math.floor((safeMillisecondsLeft / 1000) % 60),
    };
};

// Обратный отсчет до серверного обновления жизней.
const Countdown = ({variant = "dark", onComplete}) => {
    const [timeLeft, setTimeLeft] = useState(null);
    const [isSyncFailed, setIsSyncFailed] = useState(false);
    const timerRef = useRef(null);
    const lastCompleteNotifyAtRef = useRef(-10000);
    const onCompleteRef = useRef(onComplete);
    const timeSyncRef = useRef({
        targetTime: 0,
        serverTime: 0,
        syncedAt: 0,
    });

    const calculateTimeLeft = useCallback(() => {
        const {targetTime, serverTime, syncedAt} = timeSyncRef.current;
        const currentServerTime = serverTime + (performance.now() - syncedAt);

        return buildTimeLeft(targetTime - currentServerTime);
    }, []);

    useEffect(() => {
        onCompleteRef.current = onComplete;
    }, [onComplete]);

    useEffect(() => {
        let isMounted = true;

        const refreshTimeLeft = () => {
            const nextTimeLeft = calculateTimeLeft();
            setTimeLeft(nextTimeLeft);

            if (
                nextTimeLeft.hours === 0 &&
                nextTimeLeft.minutes === 0 &&
                nextTimeLeft.seconds === 0 &&
                performance.now() - lastCompleteNotifyAtRef.current >= 10000
            ) {
                lastCompleteNotifyAtRef.current = performance.now();
                if (onCompleteRef.current) {
                    onCompleteRef.current();
                }
            }
        };

        // Синхронизируемся с сервером, чтобы таймер не зависел от часов устройства.
        const syncStartedAt = performance.now();
        getServerTime().then(resp => {
            if (!isMounted) {
                return;
            }

            const syncEndedAt = performance.now();
            const data = resp?.data?.response;
            const serverTime = Date.parse(data?.server_time);
            const targetTime = Date.parse(data?.next_daily_attempts_at);

            if (!Number.isFinite(serverTime) || !Number.isFinite(targetTime)) {
                setIsSyncFailed(true);
                return;
            }

            timeSyncRef.current = {
                targetTime,
                serverTime,
                syncedAt: syncStartedAt + ((syncEndedAt - syncStartedAt) / 2),
            };
            refreshTimeLeft();
            timerRef.current = setInterval(refreshTimeLeft, 1000);
        }).catch(err => {
            console.error("Не удалось синхронизировать серверное время:", err);
            if (isMounted) {
                setIsSyncFailed(true);
            }
        });

        return () => {
            isMounted = false;
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        }
    }, [calculateTimeLeft]);

    const isLight = variant === "light";

    const containerClassName = isLight
        ? "mx-4 mb-2 flex flex-col rounded-lg border border-gray-300/60 bg-gray-100/80 px-3 pt-3"
        : "mx-4 mb-2 flex flex-col rounded-lg border border-gray-300/60 bg-gray-100 px-3 pt-3";
    const titleClassName = isLight ? "text-gray-700 text-sm" : "text-gray-700 text-sm";
    const timerRowClassName = isLight
        ? "p-4 flex flex-row mb-2 justify-center text-gray-700"
        : "p-4 flex flex-row mb-2 justify-center text-gray-700";
    const digitsClassName = isLight ? "text-2xl font-bold text-gray-900" : "text-2xl font-bold text-gray-900";

    const statusText = isSyncFailed
        ? "Не удалось синхронизировать серверное время. Попробуйте обновить страницу."
        : "Синхронизируем серверное время...";
    const displayTimeLeft = timeLeft || {hours: 0, minutes: 0, seconds: 0};

    return (
        <div className={containerClassName}>
            <div className={"flex flex-row"}>
                <p className={titleClassName}>Жизни на сегодня закончились. Следующая игра будет доступна через:</p>
            </div>

            {!timeLeft && (
                <div className="p-4 mb-2 text-center text-sm text-gray-700">{statusText}</div>
            )}

            {timeLeft && (
            <div className={timerRowClassName}>
                <div className={"flex flex-col items-center justify-center h-12 w-20 px-4"}>
                    <div className={digitsClassName}>{displayTimeLeft.hours}</div>
                    <span>{ numberWordSuffix(displayTimeLeft.hours, ["час", "часа", "часов"]) }</span>
                </div>
                <div className={"flex flex-col items-center justify-center h-12 w-20 px-4"}>
                    <div className={digitsClassName}>{displayTimeLeft.minutes}</div>
                    <span>{ numberWordSuffix(displayTimeLeft.minutes, ["минуту", "минуты", "минут"]) }</span>
                </div>
                <div className={"flex flex-col items-center justify-center h-12 w-20 px-4"}>
                    <div className={digitsClassName}>{displayTimeLeft.seconds}</div>
                    <span>{ numberWordSuffix(displayTimeLeft.seconds, ["секунду", "секунды", "секунд"]) }</span>
                </div>
            </div>
            )}
        </div>
    );
}

export default Countdown;
